// ===================================================================
// kamaChapterTitles.ts — i18n display titles for Kama chapter badges
// ===================================================================
// KamaLearnMode's "Chapitre X/N · <title>" badge uses a section-header
// entry's dictionary KEY as the title (e.g. 'Voyelles conventionnelles'
// in lecture.ts, 'Le pluriel : l\'analyse' in phrase.ts) — these keys must
// never be renamed since they're also used for data lookup/progress
// tracking. This module only maps the raw key to a translated *display*
// title for the badge; callers keep using the raw key everywhere else.
import { TFunction } from 'i18next';

const KAMA_CHAPTER_TITLE_KEYS: Record<string, string> = {
  // categories/lecture.ts
  'Voyelles conventionnelles': 'kamaLearnMode.chapterTitles.conventionalVowels',
  'Les sons des lèvres': 'kamaLearnMode.chapterTitles.lipSounds',
  'La langue contre le palais': 'kamaLearnMode.chapterTitles.tongueAgainstPalate',
  'Les quatre h égyptiens': 'kamaLearnMode.chapterTitles.fourHSounds',
  'Les sifflantes': 'kamaLearnMode.chapterTitles.sibilants',
  'Le fond de la gorge': 'kamaLearnMode.chapterTitles.backOfThroat',
  'Les dentales, simples et palatalisées': 'kamaLearnMode.chapterTitles.dentals',
  'Au-delà de l\'alphabet': 'kamaLearnMode.chapterTitles.beyondAlphabet',
  'Lire un mot entier': 'kamaLearnMode.chapterTitles.readingAWholeWord',
  'Le déchiffrement': 'kamaLearnMode.chapterTitles.decipherment',
  // categories/phrase.ts
  'Ordre verbe-sujet-objet : l\'analyse': 'kamaLearnMode.chapterTitles.verbSubjectObjectAnalysis',
  'Les prépositions : l\'analyse': 'kamaLearnMode.chapterTitles.prepositionsAnalysis',
  'La phrase adverbiale : l\'analyse': 'kamaLearnMode.chapterTitles.adverbialSentenceAnalysis',
  'La phrase nominale : l\'analyse': 'kamaLearnMode.chapterTitles.nominalSentenceAnalysis',
  'L\'adjectif épithète : l\'analyse': 'kamaLearnMode.chapterTitles.attributiveAdjectiveAnalysis',
  'Les pronoms suffixes : l\'analyse': 'kamaLearnMode.chapterTitles.suffixPronounsAnalysis',
  'Les pronoms indépendants : l\'analyse': 'kamaLearnMode.chapterTitles.independentPronounsAnalysis',
  'Le pluriel : l\'analyse': 'kamaLearnMode.chapterTitles.pluralAnalysis',
  'Le duel : l\'analyse': 'kamaLearnMode.chapterTitles.dualAnalysis',
  'Le génitif direct : l\'analyse': 'kamaLearnMode.chapterTitles.directGenitiveAnalysis',
  'Le génitif indirect : l\'analyse': 'kamaLearnMode.chapterTitles.indirectGenitiveAnalysis',
};

// Titles absent from the map above fall back to displaying the raw key
// as-is, i.e. today's behavior — safe for any future section header added
// without an entry here.
export const displayKamaChapterTitle = (title: string, t: TFunction): string => {
  const key = KAMA_CHAPTER_TITLE_KEYS[title];
  return key ? t(key) : title;
};
